import { MaterialType, UserLocale } from '@mnd-frontend/graphql-schema';
import {
  OPENED_CONTACT_PERSON_FORM_FROM_MATERIAL_QUERY_PARAM,
  QUERY_PARAM_FOR_PREFILLED_TAG_SEARCH,
  QUERY_PARAM_WHICH_INDICATES_INITIALLY_OPENED_MODAL,
} from '../lib/constants';
import mndUrl, { SubDomain } from '../lib/mndUrl';
import type { TemplateTypes } from '../modules/publish/components/GenerateMaterialsPage/types';
import createPath from './createPath';

export type AllUrlMaterialTypes =
  | 'images'
  | 'pressreleases'
  | 'news'
  | 'blog_posts'
  | 'events'
  | 'documents'
  | 'videos';

export type UrlRegularPublishMaterialTypes =
  | 'pressreleases'
  | 'news'
  | 'blog_posts'
  | 'events'
  | 'documents'
  | 'videos';

export const materialTypeToUrlType = (type: MaterialType): UrlRegularPublishMaterialTypes => {
  switch (type) {
    case MaterialType.BlogPost:
      return 'blog_posts';
    case MaterialType.Event:
      return 'events';
    case MaterialType.News:
      return 'news';
    case MaterialType.Pressrelease:
      return 'pressreleases';
    case MaterialType.Document:
      return 'documents';
    case MaterialType.Video:
      return 'videos';
    default:
      throw new Error(`${type} is not supported as a url type for edit`);
  }
};

export type UrlStoryType = 'pressreleases' | 'news' | 'blog_posts';

export enum NewsroomSettingsAnchors {
  BoilerplateCard = 'newsroom-settings-boilerplate-card',
}

const routes = {
  pressroomSelector: createPath('/')(),

  /**
   * Never renders anything. Only redirects to dashboard or monitor depending on plan
   */
  pressroomRoot: createPath('/:pressroomId')(),

  preview: createPath('/:pressroomId/preview')(),

  dashboard: createPath('/:pressroomId/dashboard')(),
  /**
   * Never renders anything. Only redirects to default Publish-page.
   */
  publish: createPath('/:pressroomId/publish')(),

  // Only a target pattern for <HelpPanel />
  publishPressreleases: createPath('/:pressroomId/publish/pressreleases')(),

  pitchList: createPath('/:pressroomId/publish/pitch')(),
  pitchEdit: createPath('/:pressroomId/publish/pitch/edit/:id')(),
  pitchCreate: createPath('/:pressroomId/publish/pitch/edit')(),
  pitchCreateWithParams: createPath(
    '/:pressroomId/publish/pitch/edit?returnTo=:returnTo&returnToName=:returnToName&materialType=:materialType&materialId=:materialId&contactId=:contactId&mndReachJournalistId=:mndReachJournalistId',
  )<{
    returnTo: string;
    returnToName: string;
    materialType: UrlRegularPublishMaterialTypes | '';
    materialId: string | '';
    contactId: string | '';
    mndReachJournalistId: string | '';
  }>(),

  publishList: createPath('/:pressroomId/publish/:materialType')<{
    materialType: UrlRegularPublishMaterialTypes | 'images';
  }>(),
  publishCreate: createPath('/:pressroomId/publish/:materialType/edit')<{
    pressroomId: string;
    materialType: UrlRegularPublishMaterialTypes;
  }>(),

  publishEdit: createPath('/:pressroomId/publish/:materialType/edit/:id')<{
    pressroomId: string;
    materialType: UrlRegularPublishMaterialTypes;
    id: string;
  }>(),
  publishEditWithOpenContactsModal: createPath(
    `/:pressroomId/publish/:materialType/edit/:id/?${QUERY_PARAM_WHICH_INDICATES_INITIALLY_OPENED_MODAL}=true`,
  )<{
    materialType: UrlRegularPublishMaterialTypes;
    id: string;
  }>(),
  publishGenerate: createPath('/:pressroomId/publish/:materialType/:template/generate')<{
    materialType: UrlRegularPublishMaterialTypes;
    template: TemplateTypes;
  }>(),
  // Landing page for links in ideation mails
  publishGenerateWithId: createPath('/:pressroomId/publish/:materialType/:template/generate/:id')<{
    materialType: UrlRegularPublishMaterialTypes;
    template: TemplateTypes;
    id: string;
  }>(),
  publishDistribute: createPath('/:pressroomId/publish/:materialType/distribute/:id')<{
    materialType: UrlRegularPublishMaterialTypes;
    id: string;
  }>(),
  publishConfirmation: createPath('/:pressroomId/publish/:materialType/confirmation/:id')<{
    materialType: UrlRegularPublishMaterialTypes;
    id: string;
  }>(),

  publishImagesList: createPath('/:pressroomId/publish/images')(),
  publishImagesEdit: createPath('/:pressroomId/publish/images/edit/:ids')(),

  publishContactPeopleList: createPath('/:pressroomId/publish/contact_people')(),
  publishContactPeopleCreate: createPath('/:pressroomId/publish/contact_people/edit')(),
  publishContactPeopleCreateWithMaterialConnection: createPath(
    `/:pressroomId/publish/contact_people/edit?${OPENED_CONTACT_PERSON_FORM_FROM_MATERIAL_QUERY_PARAM}=:material&isDraft=:isDraft`,
  )<{
    material: `${MaterialType}|${string}`;
    isDraft: string;
  }>(),
  publishContactPeopleEdit: createPath('/:pressroomId/publish/contact_people/edit/:id')(),
  publishIdeas: createPath('/:pressroomId/publish/ideas')(),
  tagManagement: createPath('/:pressroomId/tag_management')(),
  tagManagementWithPrefilledSearch: createPath(
    `/:pressroomId/tag_management?${QUERY_PARAM_FOR_PREFILLED_TAG_SEARCH}=:tagName`,
  )<{ tagName: string }>(),
  accountUserManagement: createPath('/:pressroomId/account/users')(),
  accountSecurity: createPath('/:pressroomId/account/security')(),

  storyWidget: createPath('/:pressroomId/story_widget')(),
  storyWidgetTab: createPath('/:pressroomId/story_widget/:tab')<{
    tab: 'preview' | 'implement';
  }>(),

  emailSettings: createPath('/:pressroomId/email_settings')(),
  emailSettingsTab: createPath('/:pressroomId/email_settings/:tab')<{
    tab: 'design';
  }>(),

  emailReports: createPath('/:pressroomId/email_reports')(),
  /**
   * Never renders anything. Only redirects to default Analyze-page.
   */
  analyze: createPath('/:pressroomId/analyze')(),
  analyzeUpgrade: createPath('/:pressroomId/analyze/upgrade')(),
  analyzeMaterialList: createPath('/:pressroomId/analyze/materials')(),
  analyzeMaterialListOfType: createPath('/:pressroomId/analyze/materials/:materialType')<{
    materialType: AllUrlMaterialTypes;
  }>(),
  /**
   * The following 2 routes are used both on `reports.mynewsdesk.com` and on `publish.mynewsdesk.com`.
   */
  analyzeMaterial: createPath('/:pressroomId/analyze/materials/:materialType/:id')<{
    materialType: AllUrlMaterialTypes;
    id: string;
  }>(),
  analyzeMaterialTab: createPath('/:pressroomId/analyze/materials/:materialType/:id/:tab')<{
    materialType: AllUrlMaterialTypes;
    id: string;
    tab: 'media_hits' | 'email_reach';
  }>(),
  /**
   * This route is used both on `reports.mynewsdesk.com` and on `publish.mynewsdesk.com`.
   */
  analyzeNewsroom: createPath('/:pressroomId/analyze/newsroom')(),
  /**
   * @deprecated use `analyzeNewsroom` instead
   */
  analyzeNewsroomDeprecated: createPath('/:pressroomId/analyze/newsroom/:filter')<{
    filter: '7days' | '30days' | 'range';
  }>(),
  /**
   * @deprecated use `analyzeNewsroom` instead
   */ analyzeNewsroomDeprecated2: createPath('/:pressroomId/analyze/newsroom/:filter/:fromDate')<{
    filter: '7days' | '30days' | 'range';
    fromDate: string;
  }>(),
  /**
   * @deprecated use `analyzeNewsroom` instead
   */ analyzeNewsroomDeprecated3: createPath(
    '/:pressroomId/analyze/newsroom/:filter/:fromDate/:toDate',
  )<{
    filter: '7days' | '30days' | 'range';
    fromDate: string;
    toDate: string;
  }>(),
  analyzeOverview: createPath('/:pressroomId/analyze/overview')(),
  newsroomSettings: createPath('/:pressroomId/settings/')(),
  newsroomSettingsWithAnchor: createPath('/:pressroomId/settings/#:anchor')<{
    anchor: NewsroomSettingsAnchors;
  }>(),
  newsroomSettingsTab: createPath('/:pressroomId/settings/:tab')<{
    tab: 'social' | 'hosted' | 'beta';
  }>(),

  contacts: createPath('/:pressroomId/contacts')(),
  contactsAll: createPath('/:pressroomId/contacts/all')(),
  contactsInactive: createPath('/:pressroomId/contacts/inactive')(),
  contactListShow: createPath('/:pressroomId/contacts/lists/:id')(),
  contactShow: createPath('/:pressroomId/contacts/contact/:id')(),
  contactShowWithReturnTo: createPath('/:pressroomId/contacts/contact/:id/?returnTo=:returnTo')<{
    id: string;
    returnTo: string;
  }>(),
  /**
   * Defaults to File Import
   */
  contactImport: createPath('/:pressroomId/contacts/import')(),
  contactImportTab: createPath('/:pressroomId/contacts/import/:tab')<{
    tab: 'paste' | 'status';
  }>(),
  contactAudienceBuilderMndReach: createPath('/:pressroomId/contacts/audience_builder')(),
  contactAudienceBuilder: createPath('/:pressroomId/contacts/audience_builder/:tab')<{
    tab: 'search' | 'advanced';
  }>(),
  contactAudienceTargeting: createPath('/:pressroomId/contacts/audience_targeting')(),
  contactAudienceTargetingMaterial: createPath(
    '/:pressroomId/contacts/audience_targeting/:materialType/:materialId',
  )<{ materialType: UrlStoryType; materialId: string }>(),

  /**
   * Never renders anything. Only redirects to default Monitor-page.
   */
  monitor: createPath('/:pressroomId/monitor')(),
  monitorStartPage: createPath('/:pressroomId/monitor/start')(),

  /**
   * Defaults to Media Feed.
   */
  monitorUpgrade: createPath('/:pressroomId/monitor/upgrade')(),
  monitorInsights: createPath('/:pressroomId/monitor/insights')(),
  monitorInsightsTab: createPath('/:pressroomId/monitor/insights/:tab')<{
    tab: 'media_impact' | 'reach' | 'impressions';
  }>(),
  monitorSearchProfiles: createPath('/:pressroomId/monitor/search_profiles')(),
  monitorSearchProfilesCreateBrand: createPath(
    '/:pressroomId/monitor/search_profiles/brand/edit',
  )(),
  monitorSearchProfilesOnboarding: createPath('/:pressroomId/monitor/search_profiles/onboarding')(),
  monitorSearchProfilesEditBrand: createPath(
    '/:pressroomId/monitor/search_profiles/brand/edit/:id',
  )(),
  monitorSearchProfilesCreateBoolean: createPath(
    '/:pressroomId/monitor/search_profiles/boolean/edit',
  )(),
  monitorSearchProfilesEditBoolean: createPath(
    '/:pressroomId/monitor/search_profiles/boolean/edit/:id',
  )(),
  monitorSearchProfilesCreateUntyped: createPath('/:pressroomId/monitor/search_profiles/edit')(),
  monitorSearchProfilesEditUntyped: createPath('/:pressroomId/monitor/search_profiles/edit/:id')(),
  monitorNewsAlertsList: createPath('/:pressroomId/monitor/news_alerts')(),
  monitorNewsAlertsCreate: createPath('/:pressroomId/monitor/news_alerts/edit')(),
  monitorNewsAlertsEdit: createPath('/:pressroomId/monitor/news_alerts/edit/:id')(),
  monitorAlertsList: createPath('/:pressroomId/monitor/alerts')(),
  monitorAlertsEdit: createPath('/:pressroomId/monitor/alerts/edit/:id')(),
  monitorAlertsCreate: createPath('/:pressroomId/monitor/alerts/new')(),
  monitorClipboardList: createPath('/:pressroomId/monitor/clipbooks')(),
  monitorClipboardCreate: createPath('/:pressroomId/monitor/clipbooks/edit')(),
  monitorClipboardEdit: createPath('/:pressroomId/monitor/clipbooks/edit/:id')(),
  monitorClipboardShow: createPath('/:pressroomId/monitor/clipbooks/:id')(),
  channels: createPath('/:pressroomId/channels')(),
  channelsEdit: createPath('/:pressroomId/channels/:id')(),
  channelsConnect: createPath('/:pressroomId/channels/connect/:provider')<{
    provider: 'facebook' | 'linkedin' | 'youtube' | 'twitter';
  }>(),

  reports: createPath('/:pressroomId/reports')(),
  reportsCreate: createPath('/:pressroomId/reports/create', '/create')(),
  reportsCreateBrand: createPath('/:pressroomId/reports/create_brand', '/create_brand')(),

  reportsCreateBrandFromSimpleSearchProfile: createPath(
    '/:pressroomId/reports/create_brand/:simpleSearchProfileId',
    '/create_brand/:simpleSearchProfileId',
  )(),
  reportsList: createPath('/:pressroomId/reports/list', '/list')(),
  reportsById: createPath('/:pressroomId/reports/:id/*', '/:id/*')(),
  reportsEdit: createPath('/:pressroomId/reports/:id/edit', '/edit')(),
  reportsPreview: createPath('/:pressroomId/reports/:id/preview', '/preview')(),
  reportsPreviewSection: createPath(
    '/:pressroomId/reports/:id/preview/:sectionId',
    '/preview/:sectionId',
  )(),
  reportsSlideshow: createPath('/:pressroomId/reports/:id/slideshow', '/slideshow')(),
  reportsUpsell: createPath('/:pressroomId/reports/upgrade', '/upgrade')(),

  accountSettings: createPath('/:pressroomId/account_settings')(),
  accountSettingsCancelSubscription: createPath(
    '/:pressroomId/account_settings/cancel_subscription',
  )(),

  copilotSettings: createPath('/:pressroomId/copilot_settings')(),

  /**
   * Without pressroom id, press officers without any pressrooms
   * (maybe they have been removed) should still be able to remove themselves.
   */
  userSettings: createPath('/user_settings')(),
  userSettingsRoot: createPath('/user_settings')(),
  userSettings2FaSetup: createPath('/user_settings/2fa')(),
  userSettingsNewCodes: createPath('/user_settings/new_recovery')(),
  userSettingsForgotPassword: createPath('/user_settings/forgot_password')(),

  userSettingsWithPressroom: createPath('/:pressroomId/user_settings')(),
  userSettingsWithPressroomRoot: createPath('/:pressroomId/user_settings')(),
  userSettingsWithPressroom2FaSetup: createPath('/:pressroomId/user_settings/2fa')(),
  userSettingsWithPressroomNewCodes: createPath('/:pressroomId/user_settings/new_recovery')(),
  userSettingsWithPressroomForgotPassword: createPath(
    '/:pressroomId/user_settings/forgot_password',
  )(),

  /**
   * All routes below are "external" in the sence of "outside of mnd-publish-frontend".
   */
  external: {
    accessibilityStatement: (userLocale: UserLocale | null | undefined) => {
      switch (userLocale) {
        case UserLocale.Sv:
          return 'https://www.mynewsdesk.com/se/about/terms-and-conditions/accessibility_statement';
        default:
          // Default to English
          return 'https://www.mynewsdesk.com/about/terms-and-conditions/accessibility_statement';
      }
    },
    googleFontsCss: (query: string) => `/google_fonts/css?${query}`,
    help: (userLocale: UserLocale | null | undefined) => {
      switch (userLocale) {
        case UserLocale.Sv:
          return 'https://help.mynewsdesk.com/sv';
        case UserLocale.De:
          return 'https://help.mynewsdesk.com/de';
        default:
          return 'https://help.mynewsdesk.com/en';
      }
    },
    helpServicesAndTraining: (userLocale: UserLocale | null | undefined) => {
      switch (userLocale) {
        case UserLocale.Sv:
          return 'https://help.mynewsdesk.com/sv/tjanster-och-utbildning';
        case UserLocale.De:
          return 'https://help.mynewsdesk.com/de/services-und-trainings';
        default:
          return 'https://help.mynewsdesk.com/en/services-and-training';
      }
    },
    helpContactSupport: (userLocale: UserLocale | null | undefined) => {
      switch (userLocale) {
        case UserLocale.Sv:
          return 'https://help.mynewsdesk.com/sv/contact';
        case UserLocale.De:
          return 'https://help.mynewsdesk.com/de/contact';
        default:
          return 'https://help.mynewsdesk.com/en/contact';
      }
    },
    howToAnalyzeOverview: 'https://help.mynewsdesk.com/faq/analyze-overview',
    howToAudienceTarget: 'https://help.mynewsdesk.com/faq/audience-targeting',
    howToAudienceBuilder:
      'https://help.mynewsdesk.com/faq/find-and-import-contacts-from-audience-builder',
    howToFindAndImportContacts:
      'https://help.mynewsdesk.com/faq/find-and-import-contacts-from-audience-builder',
    howToPublishPressrelease:
      'https://help.mynewsdesk.com/faq/publish-a-press-release-news-or-blog-post',
    howToIntroductionToContacts: 'https://help.mynewsdesk.com/faq/introduction-to-contacts',
    library: (userLocale: UserLocale | null | undefined) => {
      switch (userLocale) {
        case UserLocale.Da:
          return mndUrl(SubDomain.WWW, '/dk-da/vidensbank');
        case UserLocale.De:
          return mndUrl(SubDomain.WWW, '/de-de/bibliothek');
        case UserLocale.No:
          return mndUrl(SubDomain.WWW, '/no-no/bibliotek');
        case UserLocale.Sv:
          return mndUrl(SubDomain.WWW, '/se-sv/bibliotek');
        default:
          return mndUrl(SubDomain.WWW, '/en/library');
      }
    },
    pricing: (userLocale: UserLocale | null | undefined) => {
      switch (userLocale) {
        case UserLocale.Da:
          return mndUrl(SubDomain.WWW, '/dk-da/abonnementer');
        case UserLocale.De:
          return mndUrl(SubDomain.WWW, '/de-de/pakete');
        case UserLocale.No:
          return mndUrl(SubDomain.WWW, '/no-no/priser');
        case UserLocale.Sv:
          return mndUrl(SubDomain.WWW, '/se-sv/priser');
        default:
          return mndUrl(SubDomain.WWW, '/en/prices');
      }
    },
    howToPublish: 'https://help.mynewsdesk.com/faq/publish-a-press-release-news-or-blog-post',
    howToCreateEngagingContent:
      'https://www.mynewsdesk.com/en/blog/5-ways-to-create-more-engaging-content/',
    coPilotReadMore: 'https://help.mynewsdesk.com/faq/copilot',
  },
};

export default routes;
